import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  InputAdornment,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Event, EventType, EventStatus, EventImpact } from "../types/event";

const EVENT_TYPE_OPTIONS: { value: EventType; label: string }[] = [
  { value: "ENTRY_SIGNAL", label: "Entry Signal" },
  { value: "EXIT_SIGNAL", label: "Exit Signal" },
  { value: "NEWS_EVENT", label: "News Event" },
  { value: "EARNINGS_REPORT", label: "Earnings Report" },
  { value: "TECHNICAL_BREAKOUT", label: "Technical Breakout" },
  { value: "SUPPORT_RESISTANCE", label: "Support/Resistance" },
  { value: "FUNDAMENTAL_ANALYSIS", label: "Fundamental Analysis" },
  { value: "MARKET_SENTIMENT", label: "Market Sentiment" },
  { value: "CUSTOM", label: "Custom" },
];

const EVENT_STATUS_OPTIONS: { value: EventStatus; label: string }[] = [
  { value: "PENDING", label: "Pending" },
  { value: "COMPLETED", label: "Completed" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "EXPIRED", label: "Expired" },
];

const EVENT_IMPACT_OPTIONS: { value: EventImpact; label: string }[] = [
  { value: "POSITIVE", label: "Positive" },
  { value: "NEGATIVE", label: "Negative" },
  { value: "NEUTRAL", label: "Neutral" },
  { value: "MIXED", label: "Mixed" },
];

export interface EventFormValues {
  id?: number;
  tradeId?: number;
  type: string;
  date: Date | null;
  description?: string;
  probability?: number;
  projectedIncrease?: number;
  targetPrice?: number;
  stopLoss?: number;
  eventType?: EventType;
  status?: EventStatus;
  impact?: EventImpact;
}

interface EventFormDialogProps {
  open: boolean;
  initialValues?: EventFormValues;
  onClose: () => void;
  onSave: (values: EventFormValues) => void;
}

export default function EventFormDialog({
  open,
  initialValues,
  onClose,
  onSave,
}: EventFormDialogProps) {
  const [form, setForm] = useState<EventFormValues>({
    type: "note",
    date: new Date(),
    ...initialValues,
  });

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      ...initialValues,
      date: initialValues?.date ? new Date(initialValues.date) : new Date(),
    }));
  }, [initialValues, open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]:
        type === "number" ? (value === "" ? undefined : Number(value)) : value,
    }));
  };

  const handleDateChange = (date: Date | null) => {
    setForm((prev) => ({ ...prev, date }));
  };

  const handleSave = () => {
    if (form.date && form.type) {
      onSave(form);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{form.id ? "Edit Event" : "Add Event"}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <TextField
            select
            label="Event Type"
            name="eventType"
            value={form.eventType || ""}
            onChange={handleChange}
            fullWidth
            margin="normal"
          >
            {EVENT_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Status"
            name="status"
            value={form.status || ""}
            onChange={handleChange}
            fullWidth
            margin="normal"
          >
            {EVENT_STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Impact"
            name="impact"
            value={form.impact || ""}
            onChange={handleChange}
            fullWidth
            margin="normal"
          >
            {EVENT_IMPACT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Type (Legacy/Custom)"
            name="type"
            value={form.type}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
          <DatePicker
            label="Date"
            value={form.date}
            onChange={handleDateChange}
            slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
          />
          <TextField
            label="Description"
            name="description"
            value={form.description || ""}
            onChange={handleChange}
            fullWidth
            margin="normal"
            multiline
            minRows={2}
          />
          <TextField
            label="Probability (%)"
            name="probability"
            type="number"
            value={form.probability ?? ""}
            onChange={handleChange}
            fullWidth
            margin="normal"
            inputProps={{ min: 0, max: 100 }}
          />
          <TextField
            label="Projected Increase"
            name="projectedIncrease"
            type="number"
            value={form.projectedIncrease ?? ""}
            onChange={handleChange}
            fullWidth
            margin="normal"
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
          />
          <TextField
            label="Target Price"
            name="targetPrice"
            type="number"
            value={form.targetPrice ?? ""}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Stop Loss"
            name="stopLoss"
            type="number"
            value={form.stopLoss ?? ""}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
